/**
 * NOTES — the thing a shop actually writes down.
 *
 * Anthony: "need to add notes to the leads, contact cards across the
 * different places where the six tile lives ... allow for notes to be
 * added or edited and record the date, time and who added note. Can add
 * photo to note as well please"
 *
 * Before this there was one `notes` column on a customer, one on a lead
 * and one on a job: a single blob per record, no author, no time, and the
 * second person to write in it quietly replaced the first. That is not a
 * note, it is a whiteboard nobody wipes on purpose.
 *
 * A note here is a record with a person and a moment on it, it can be
 * edited without losing the fact that it was edited, and it can carry
 * photographs — which is how half of what a shop knows actually arrives.
 *
 * WHAT IT IS ABOUT is one or more of four things, and a note keeps all of
 * them. A note written on a lead that becomes a job carries its enquiry_id
 * AND its job_id, so it shows up on the job without disappearing from the
 * lead it was written on.
 */

import { db } from "./client.ts";
import { shrink } from "./photos.ts";
import { signedPhoto } from "./photolinks.ts";

export type NoteTarget = {
  jobId?: string | null;
  customerId?: string | null;
  enquiryId?: string | null;
  vehicleId?: string | null;
};

export type NotePhoto = { id: string; url: string | null; path: string | null };

export type Note = {
  id: string;
  body: string;
  author_id: string | null;
  author_name: string;
  created_at: string;
  edited_at: string | null;
  edited_name: string | null;
  job_id: string | null;
  customer_id: string | null;
  enquiry_id: string | null;
  vehicle_id: string | null;
  /** Profile ids tagged with @. See data/mentions.ts and migration 0089. */
  mentions: string[];
  photos: NotePhoto[];
};

const SEL = "id,body,author_id,author_name,created_at,edited_at,edited_name,"
  + "job_id,customer_id,enquiry_id,vehicle_id,mentions,photos(id,url,path)";

const BUCKET = "job-photos";

/**
 * THE CODE SHIPS BEFORE THE TABLE EXISTS, and has to survive the gap.
 *
 * A deploy is one push and a migration is a separate hand-run command, so
 * for the minutes or hours between them this table is not there. The rest
 * of this file already reports its errors honestly; this one error is the
 * exception, because "relation notes does not exist" in a red box on three
 * screens is a bug report from every person in the shop about something
 * nobody can act on. The card hides itself instead, and says nothing.
 *
 * 42P01 is Postgres. PGRST205 is PostgREST answering before Postgres gets
 * a look in, from its own schema cache.
 */
export function notesNotBuiltYet(e: unknown): boolean {
  const c = (e as { code?: string; message?: string } | null);
  return c?.code === "42P01" || c?.code === "PGRST205"
    || /relation .*notes.* does not exist|find the table/i.test(String(c?.message ?? ""));
}

/** Who is writing, and which shop they write for. */
/** Exported so the notes card can decide not to ring your own bell. */
export async function whoAmI(): Promise<{ id: string | null; name: string; accountId: string }> {
  return me();
}

async function me(): Promise<{ id: string | null; name: string; accountId: string }> {
  const sb = await db();
  const { data: auth } = await sb.auth.getUser();
  const id = auth.user?.id ?? null;
  const { data: prof } = await sb.from("profiles")
    .select("account_id,full_name").eq("id", id ?? "").maybeSingle();
  const p = prof as { account_id: string; full_name: string | null } | null;
  if (!p?.account_id) throw new Error("Cannot tell which shop you belong to. Sign in again.");
  return { id, name: p.full_name?.trim() || "somebody", accountId: p.account_id };
}

/**
 * EVERY NOTE THAT MENTIONS THIS THING, newest first.
 *
 * An OR rather than one column, because a job and its lead and its
 * customer are three views of one conversation. Somebody opening the job
 * wants what was said on the phone before it was a job, and having to
 * remember which screen it was typed into is the failure this replaces.
 *
 * The customer is deliberately NOT pulled in on a job: a customer note is
 * about the person across every car they have ever brought in, and
 * repeating it under all nine of their jobs buries the note about today.
 */
export async function listNotes(t: NoteTarget, limit = 50): Promise<Note[]> {
  const sb = await db();
  const ors: string[] = [];
  if (t.jobId) ors.push(`job_id.eq.${t.jobId}`);
  if (t.customerId) ors.push(`customer_id.eq.${t.customerId}`);
  if (t.enquiryId) ors.push(`enquiry_id.eq.${t.enquiryId}`);
  if (t.vehicleId) ors.push(`vehicle_id.eq.${t.vehicleId}`);
  if (!ors.length) return [];

  const { data, error } = await sb.from("notes").select(SEL)
    .or(ors.join(","))
    .order("created_at", { ascending: false }).limit(limit);
  if (error) {
    const err = new Error(error.message) as Error & { code?: string };
    err.code = error.code;
    throw err;
  }
  return (data ?? []) as unknown as Note[];
}

export async function addNote(
  t: NoteTarget, body: string, mentions: string[] = [],
): Promise<Note> {
  const text = body.trim();
  if (!text) throw new Error("An empty note is not a note.");
  const sb = await db();
  const who = await me();

  const { data, error } = await sb.from("notes").insert({
    mentions,
    account_id: who.accountId,
    job_id: t.jobId ?? null,
    customer_id: t.customerId ?? null,
    enquiry_id: t.enquiryId ?? null,
    vehicle_id: t.vehicleId ?? null,
    body: text,
    author_id: who.id,
    author_name: who.name,
  }).select(SEL).single();
  if (error) throw new Error(error.message);
  return data as unknown as Note;
}

/**
 * AN EDIT SAYS IT WAS AN EDIT.
 *
 * The original author stays the author — they wrote it — and the person
 * who changed it is recorded beside them. A note that silently changes
 * under somebody's name is worse than no note, because it is evidence
 * that reads as first-hand and is not.
 */
export async function editNote(id: string, body: string): Promise<Note> {
  const text = body.trim();
  if (!text) throw new Error("An empty note is not a note. Delete it instead.");
  const sb = await db();
  const who = await me();

  const { data, error } = await sb.from("notes").update({
    body: text,
    edited_at: new Date().toISOString(),
    edited_by: who.id,
    edited_name: who.name,
  }).eq("id", id).select(SEL).single();
  if (error) throw new Error(error.message);
  return data as unknown as Note;
}

export async function deleteNote(id: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("notes").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * A PHOTO ONTO A NOTE.
 *
 * The same shrink-on-the-phone path every other photo takes, because a
 * note photographed off a customer's phone is a 5MB file taken on a bad
 * signal exactly like the ones under a car. It is filed as a real photo
 * row as well as a note attachment, so a picture of a leaking hose taken
 * while writing a note still turns up on the job's roll where somebody
 * looking for photos would expect it.
 */
export async function addNotePhoto(note: Note, file: File): Promise<NotePhoto> {
  const sb = await db();
  const who = await me();

  const blob = await shrink(file);
  const type = blob.type || file.type || "image/jpeg";
  const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
  if (!ALLOWED.includes(type)) {
    throw new Error(
      "This phone saved the photo in a format the system cannot read. "
      + "In Settings > Camera, set Formats to Most Compatible, then take it again.");
  }
  const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
  const stamp = `${Date.now()}-${Math.round(performance.now())}`;
  // Filed under the job when there is one, so a shop browsing storage sees
  // the same shape as every other photo; under the note when there is not.
  const where = note.job_id ? `${note.job_id}/note` : `notes/${note.id}`;
  const path = `${who.accountId}/${where}/${stamp}.${ext}`;

  const up = await sb.storage.from(BUCKET).upload(path, blob, { contentType: type, upsert: false });
  if (up.error) throw new Error(`The photo did not upload: ${up.error.message}`);

  const { data, error } = await sb.from("photos").insert({
    account_id: who.accountId,
    note_id: note.id,
    job_id: note.job_id,
    vehicle_id: note.vehicle_id,
    customer_id: note.customer_id,
    path, kind: "general",
    taken_by: who.id,
  }).select("id,url,path").single();

  if (error) {
    // Without the row the file is litter. Take it back out.
    await sb.storage.from(BUCKET).remove([path]);
    throw new Error(error.message);
  }
  return data as unknown as NotePhoto;
}

/** A stored path becomes a signed link; an imported row already has a URL. */
export async function noteImageSrc(p: NotePhoto): Promise<string> {
  if (p.url) return p.url;
  if (!p.path) return "";
  return signedPhoto(p.path);
}
